import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Avatar,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  Grid,
  Paper,
  Typography,
  Chip,
  Switch,
  InputAdornment
} from '@mui/material';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { updateProfile, updatePassword } from 'firebase/auth';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { auth, db, storage } from '../firebase';

const AdminProfileDialog = ({ open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    photoURL: '',
    documents: {
      idProofUrl: '',
      resumeUrl: '',
      certificatesUrl: '',
      verificationVideoUrl: ''
    }
  });
  const [newPassword, setNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (open) loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const loadProfile = async () => {
    const user = auth.currentUser;
    if (!user) return;
    setLoading(true);
    try {
      const docRef = doc(db, 'users', user.uid);
      const snap = await getDoc(docRef);
      if (snap.exists()) {
        const data = snap.data();
        setForm(prev => ({
          ...prev,
          name: data.name || user.displayName || '',
          email: data.email || user.email || '',
          phone: data.phone || data.mobile || '',
          photoURL: data.photoURL || user.photoURL || '',
          documents: {
            ...prev.documents,
            ...(data.documents || {}),
            ...(data.provider_onboarding?.documents || {}),
            ...(data.details?.provider_onboarding?.documents || {})
          }
        }));
      } else {
        // fallback to auth profile
        setForm(prev => ({ ...prev, name: user.displayName || '', email: user.email || '', phone: '', photoURL: user.photoURL || '' }));
      }
    } catch (err) {
      console.error('Load profile error', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const user = auth.currentUser;
    if (!user) return setSnackbar({ open: true, message: 'Not signed in', severity: 'error' });
    setLoading(true);
    try {
      const storageRef = ref(storage, `avatars/${user.uid}_${Date.now()}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      setForm(prev => ({ ...prev, photoURL: url }));
      setSnackbar({ open: true, message: 'Image uploaded', severity: 'success' });
    } catch (err) {
      console.error('Upload failed', err);
      setSnackbar({ open: true, message: 'Upload failed', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const user = auth.currentUser;
    if (!user) return setSnackbar({ open: true, message: 'Not signed in', severity: 'error' });
    setSaving(true);
    try {
      // Update Firestore user doc
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        photoURL: form.photoURL,
        documents: form.documents,
        updatedAt: serverTimestamp()
      });

      // Update auth profile (displayName / photoURL)
      await updateProfile(user, { displayName: form.name, photoURL: form.photoURL || null });
      // Reload auth user so AuthContext/onAuthStateChanged sees the updated photoURL immediately
      try {
        if (auth.currentUser && auth.currentUser.reload) await auth.currentUser.reload();
      } catch (reloadErr) {
        console.warn('Failed to reload auth currentUser after profile update', reloadErr);
      }

      // Password change (optional)
      if (newPassword && newPassword.length >= 6) {
        try {
          await updatePassword(user, newPassword);
          setSnackbar({ open: true, message: 'Profile and password updated', severity: 'success' });
        } catch (pwErr) {
          // password update may require recent login
          console.error('Password update failed', pwErr);
          setSnackbar({ open: true, message: 'Profile updated, password change failed (reauth required)', severity: 'warning' });
        }
      } else {
        setSnackbar({ open: true, message: 'Profile updated', severity: 'success' });
      }

      onClose();
    } catch (err) {
      console.error('Save profile failed', err);
      setSnackbar({ open: true, message: 'Save failed', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton size="small" onClick={onClose}><ArrowBackIosNewIcon fontSize="small" /></IconButton>
            <Box>
              <Typography variant="h6">Admin Profile</Typography>
              <Typography variant="body2" color="text.secondary">View and manage account details</Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
            <Avatar src={form.photoURL} sx={{ width: 88, height: 88 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6">{form.name || '—'}</Typography>
              <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                <Chip label="ADMIN" size="small" />
                <Chip label="Active" size="small" color="success" />
              </Box>
            </Box>
            <Box>
              <input accept="image/*" id="avatar-upload" type="file" style={{ display: 'none' }} onChange={handleFile} />
              <label htmlFor="avatar-upload">
                <IconButton component="span">
                  <PhotoCamera />
                </IconButton>
              </label>
            </Box>
          </Box>

          {loading ? <CircularProgress /> : (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>Personal Information</Typography>
                  <TextField label="Full name" value={form.name} onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))} fullWidth sx={{ mb: 1 }} />
                  <TextField label="Email" value={form.email} onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))} fullWidth sx={{ mb: 1 }} />
                  <TextField label="Phone" value={form.phone} onChange={(e) => setForm(prev => ({ ...prev, phone: e.target.value }))} fullWidth sx={{ mb: 1 }} />
                  <TextField
                    label="New Password (min 6 chars)"
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            edge="end"
                            onClick={() => setShowNewPassword((prev) => !prev)}
                            aria-label="toggle password visibility"
                          >
                            {showNewPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>Documents</Typography>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <TextField label="ID Proof URL" value={form.documents.idProofUrl || ''} onChange={(e) => setForm(prev => ({ ...prev, documents: { ...prev.documents, idProofUrl: e.target.value } }))} fullWidth />
                    {form.documents.idProofUrl ? (
                      <IconButton component="a" href={form.documents.idProofUrl} target="_blank" rel="noreferrer"><OpenInNewIcon /></IconButton>
                    ) : null}
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <TextField label="Resume URL" value={form.documents.resumeUrl || ''} onChange={(e) => setForm(prev => ({ ...prev, documents: { ...prev.documents, resumeUrl: e.target.value } }))} fullWidth />
                    {form.documents.resumeUrl ? (
                      <IconButton component="a" href={form.documents.resumeUrl} target="_blank" rel="noreferrer"><OpenInNewIcon /></IconButton>
                    ) : null}
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <TextField label="Certificates URL" value={form.documents.certificatesUrl || ''} onChange={(e) => setForm(prev => ({ ...prev, documents: { ...prev.documents, certificatesUrl: e.target.value } }))} fullWidth />
                    {form.documents.certificatesUrl ? (
                      <IconButton component="a" href={form.documents.certificatesUrl} target="_blank" rel="noreferrer"><OpenInNewIcon /></IconButton>
                    ) : null}
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField label="Verification Video URL" value={form.documents.verificationVideoUrl || ''} onChange={(e) => setForm(prev => ({ ...prev, documents: { ...prev.documents, verificationVideoUrl: e.target.value } }))} fullWidth />
                    {form.documents.verificationVideoUrl ? (
                      <IconButton component="a" href={form.documents.verificationVideoUrl} target="_blank" rel="noreferrer"><OpenInNewIcon /></IconButton>
                    ) : null}
                  </Box>
                </Paper>
              </Grid>

            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ open: false, message: '', severity: 'success' })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ open: false, message: '', severity: 'success' })}>{snackbar.message}</Alert>
      </Snackbar>
    </>
  );
};

export default AdminProfileDialog;
